import { inject, Injectable } from '@angular/core';
import { DesignTokenEventsRepository } from '@tokenbox/database';
import { AuthService } from '@tokenbox/services';
import { SelectedBranchService } from '@tokenbox/services';
import {
  combineLatestWith,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  switchMap,
} from 'rxjs';
import { DesignTokenEvent } from '@tokenbox/domain';
import { toSignal } from '@angular/core/rxjs-interop';
import { WebAppDesignTokensStateService } from './web-app-design-tokens-state.service.ts';

export type DesignTokensHistoryEvent = Awaited<
  ReturnType<DesignTokenEventsRepository['getEventHistoryPage']>
>[number];

@Injectable({
  providedIn: 'root',
})
export class WorkspaceHistoryService {
  private authService = inject(AuthService);
  private selectedBranchService = inject(SelectedBranchService);
  private designTokensStateService = inject(WebAppDesignTokensStateService);
  private designTokenEventsRepository = inject(DesignTokenEventsRepository);

  // TODO: nach jedem event (e.g. token name change) wird getEventHistoryPage aufgerufen (wegen undo/redo service). kann man hier die datenmengen reduzieren? --> fix!
  public readonly eventHistoryOfSelectedBranch$: Observable<
    DesignTokensHistoryEvent[]
  > = this.authService.isLoggedIn$.pipe(
    filter(Boolean),
    switchMap(() => this.selectedBranchService.selectedBranchId$),
    filter(Boolean),
    switchMap((branchId) =>
      merge(this.designTokensStateService.designTokensState$).pipe(
        debounceTime(100),
        switchMap(() =>
          this.designTokenEventsRepository.getEventHistoryPage(
            branchId,
            0,
            500,
          ),
        ),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  public readonly eventHistoryOfSelectedBranch = toSignal(
    this.eventHistoryOfSelectedBranch$,
    { initialValue: [] },
  );

  public readonly historyOfMyEvents$ = this.eventHistoryOfSelectedBranch$.pipe(
    combineLatestWith(this.authService.userId$),
    map(([events, userId]) =>
      events.filter((event) => event.creator.id === userId),
    ),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  public readonly historyOfMyEvents = toSignal(this.historyOfMyEvents$, {
    initialValue: [],
  });

  async getTotalLengthOfEventHistory(branchId: string) {
    return this.designTokenEventsRepository.getTotalLengthOfEventHistory(
      branchId,
    );
  }

  async getEventHistoryPage(
    branchId: string,
    first: number,
    rows: number,
  ): Promise<DesignTokensHistoryEvent[]> {
    return await this.designTokenEventsRepository.getEventHistoryPage(
      branchId,
      first,
      rows,
    );
  }

  async getEvent(eventId: string): Promise<DesignTokenEvent> {
    return this.designTokenEventsRepository.getEvent(eventId);
  }
}
